import { datadogRum } from '@datadog/browser-rum';

let objectInitialized = false;

// Simple error filtering - just add patterns here
const IGNORED_ERRORS = [
  'The user is not authenticated',
  'Token has expired',
  'canceled',
  'ERR_CANCELED'
];

const Datadog = {
  isInitialized() {
    return objectInitialized;
  },

  init() {
    if (Datadog.isInitialized()) {
      return;
    }

    datadogRum.init({
      applicationId: 'f0bcb020-1c2e-4366-b693-bbcce5726336',
      clientToken: 'pub53ccd3350f603b07a59fa315f5a61995',
      site: 'datadoghq.com',
      service: 'self-serve-testing-ui',
      datacenter: 'us',
      env: import.meta.env.MODE,
      version: process.env.PACKAGE_VERSION,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'allow',
      allowedTracingUrls: [/https:\/\/.*\.cardlytics\.com/],
      beforeSend(event) {
        if (event.type === 'error') {
          const errorMessage = event.error?.message || '';
          const errorCode = event.error?.code || '';
          if (
            IGNORED_ERRORS.some((pattern) => errorMessage.includes(pattern)) ||
            errorCode === 'ERR_CANCELED'
          ) {
            return false;
          }
        }
        return true;
      }
    });

    objectInitialized = true;
  },

  setUser({
    id,
    email,
    firstName,
    lastName
  }: {
    id: string;
    email: string | undefined;
    firstName: string | undefined;
    lastName: string | undefined;
  }) {
    if (!Datadog.isInitialized()) {
      return;
    }

    datadogRum.setUser({
      id,
      email,
      name: [firstName, lastName].join(' '),
      domain: email?.split('@')[1]
    });
  },

  setInternal(isInternal: boolean) {
    if (!Datadog.isInitialized()) {
      return;
    }

    datadogRum.setUserProperty('internal', isInternal);
  },

  setInstitution({
    id,
    name,
    codeName,
    country,
    type,
    isTest
  }: {
    id: string;
    name: string;
    codeName: string;
    country: string;
    type: string;
    isTest: boolean;
  }) {
    if (!Datadog.isInitialized()) {
      return;
    }

    datadogRum.setGlobalContextProperty('institution_id', id);
    datadogRum.setGlobalContextProperty('institution_name', name);
    datadogRum.setGlobalContextProperty('institution_code', codeName);
    datadogRum.setGlobalContextProperty('institution_country', country);
    datadogRum.setGlobalContextProperty('institution_type', type);
    datadogRum.setGlobalContextProperty('is_test_institution', isTest);
  },

  sendEvent(event: string, payload = {}) {
    if (!Datadog.isInitialized()) {
      return;
    }

    datadogRum.addAction(event, payload);
  },

  sendError(error: unknown, context = {}) {
    if (!Datadog.isInitialized()) {
      return;
    }

    // Get current global context to include institution data in each error
    const globalContext = datadogRum.getGlobalContext();
    const enrichedContext = {
      ...context,
      ...globalContext
    };

    datadogRum.addError(error, enrichedContext);
  },

  getSessionId() {
    if (!Datadog.isInitialized()) {
      return null;
    }

    const context = datadogRum.getInternalContext();
    return context ? context.session_id : null;
  }
};

export { Datadog };
